import { CONCEPTS } from "../data/concepts";
import { analects } from "./analects";
import { GATE_ORDER } from "./gates";
import { allConceptProgress, allFigureProgress, chapterProgress, rankOf } from "./mastery";
import { countPassedGates, getRecord, progressSummary } from "./progress";
import type { ProgressState } from "./types";

/**
 * 成就 — every one is earned by a *reading* milestone, not by time spent.
 * Nothing here can be obtained by clicking through the game quickly.
 */
export interface Achievement {
  id: string;
  label: string;
  /** the line that appears before you have it — states the requirement */
  requirement: string;
  /** the line that appears once you have it — usually a passage */
  earned: string;
  tier: "bronze" | "silver" | "gold";
  progress: (state: ProgressState) => { have: number; need: number };
}

const gatesOf = (state: ProgressState): number => progressSummary(state).gates;

export const ACHIEVEMENTS: Achievement[] = [
  {
    id: "first-light",
    label: "開卷",
    requirement: "破第一道門",
    earned: "學而時習之，不亦說乎",
    tier: "bronze",
    progress: (state) => ({ have: Math.min(1, gatesOf(state)), need: 1 }),
  },
  {
    id: "first-passage",
    label: "通一章",
    requirement: "把一章的四門全破",
    earned: "四門皆通，這一章是你的了",
    tier: "bronze",
    progress: (state) => ({ have: Math.min(1, progressSummary(state).mastered), need: 1 }),
  },
  {
    id: "ten-passages",
    label: "積十章",
    requirement: "通十章",
    earned: "譬如為山，未成一簣",
    tier: "bronze",
    progress: (state) => ({ have: Math.min(10, progressSummary(state).mastered), need: 10 }),
  },
  {
    id: "chapter-one",
    label: "學而已畢",
    requirement: "《學而》全篇通讀",
    earned: "首篇既通，門已入",
    tier: "silver",
    progress: (state) => {
      const chapter = chapterProgress(state, 1);
      return { have: chapter.mastered, need: chapter.total };
    },
  },
  {
    id: "streak-7",
    label: "七日不輟",
    requirement: "連續七天有修業",
    earned: "學如不及，猶恐失之",
    tier: "silver",
    progress: (state) => ({ have: Math.min(7, state.dayStreak ?? 0), need: 7 }),
  },
  {
    id: "streak-30",
    label: "三十日不輟",
    requirement: "連續三十天有修業",
    earned: "逝者如斯夫，不舍晝夜",
    tier: "gold",
    progress: (state) => ({ have: Math.min(30, state.dayStreak ?? 0), need: 30 }),
  },
  {
    id: "concept-ren",
    label: "識仁",
    requirement: "「仁」達到漸熟（50%）",
    earned: "我欲仁，斯仁至矣",
    tier: "silver",
    progress: (state) => {
      const concept = allConceptProgress(state).find((entry) => entry.concept.id === "ren")!;
      return { have: Math.min(50, concept.percent), need: 50 };
    },
  },
  {
    id: "concepts-five",
    label: "五藝旁通",
    requirement: "五個核心概念達到略通（25%）",
    earned: "君子不器",
    tier: "silver",
    progress: (state) => ({
      have: Math.min(5, allConceptProgress(state).filter((entry) => entry.percent >= 25).length),
      need: 5,
    }),
  },
  {
    id: "concepts-all",
    label: "一以貫之",
    requirement: `${CONCEPTS.length} 個核心概念全部達到漸熟（50%）`,
    earned: "吾道一以貫之",
    tier: "gold",
    progress: (state) => ({
      have: allConceptProgress(state).filter((entry) => entry.percent >= 50).length,
      need: CONCEPTS.length,
    }),
  },
  {
    id: "figures-eight",
    label: "識其門人",
    requirement: "認得八位人物（各達略通）",
    earned: "德行、言語、政事、文學，各有其人",
    tier: "silver",
    progress: (state) => ({
      have: Math.min(8, allFigureProgress(state).filter((entry) => entry.percent >= 25).length),
      need: 8,
    }),
  },
  {
    id: "no-hint-20",
    label: "不假燭照",
    requirement: "累積 200 次答對",
    earned: "知之為知之，不知為不知",
    tier: "silver",
    progress: (state) => {
      let correct = 0;
      for (const analect of analects) {
        const record = getRecord(state, analect.id);
        for (const gate of GATE_ORDER) correct += record.gates[gate].correct;
      }
      return { have: Math.min(200, correct), need: 200 };
    },
  },
  {
    id: "rank-buhuo",
    label: "四十不惑",
    requirement: "學階達到「不惑」",
    earned: "四十而不惑",
    tier: "gold",
    progress: (state) => {
      const rank = rankOf(state);
      return { have: Math.min(520, rank.gates), need: 520 };
    },
  },
  {
    id: "half-book",
    label: "半部論語",
    requirement: "通讀半部（256 章）",
    earned: "半部《論語》，已足治事",
    tier: "gold",
    progress: (state) => ({
      have: Math.min(256, progressSummary(state).mastered),
      need: 256,
    }),
  },
  {
    id: "whole-book",
    label: "全書皆通",
    requirement: "五百一十二章全通",
    earned: "不知命，無以為君子也",
    tier: "gold",
    progress: (state) => ({ have: progressSummary(state).mastered, need: analects.length }),
  },
  {
    id: "perfect-chapter",
    label: "篇篇有得",
    requirement: "二十篇每篇都至少通一章",
    earned: "由博返約，篇目已無生地",
    tier: "silver",
    progress: (state) => {
      let touched = 0;
      for (let major = 1; major <= 20; major += 1) {
        if (chapterProgress(state, major).mastered > 0) touched += 1;
      }
      return { have: touched, need: 20 };
    },
  },
  {
    id: "deep-five",
    label: "精熟五章",
    requirement: "五章的四門全部答對且無錯",
    earned: "溫故而知新，可以為師矣",
    tier: "gold",
    progress: (state) => {
      let clean = 0;
      for (const analect of analects) {
        const record = getRecord(state, analect.id);
        if (countPassedGates(record) !== GATE_ORDER.length) continue;
        const flawless = GATE_ORDER.every((gate) => {
          const srs = record.gates[gate];
          return srs.attempts > 0 && srs.attempts === srs.correct;
        });
        if (flawless) clean += 1;
      }
      return { have: Math.min(5, clean), need: 5 };
    },
  },
];

export interface AchievementState {
  achievement: Achievement;
  have: number;
  need: number;
  done: boolean;
  percent: number;
}

export function achievementStates(state: ProgressState): AchievementState[] {
  return ACHIEVEMENTS.map((achievement) => {
    const { have, need } = achievement.progress(state);
    return {
      achievement,
      have,
      need,
      done: have >= need,
      percent: need ? Math.min(100, Math.round((have / need) * 100)) : 0,
    };
  });
}

export function earnedCount(state: ProgressState): { done: number; total: number } {
  const states = achievementStates(state);
  return { done: states.filter((entry) => entry.done).length, total: states.length };
}
